import React, { useState, useEffect } from 'react';
import { X, Star, Send, Gift, Heart } from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export const ReviewRequestPopup = ({ onClose, onSuccess }) => {
  const [reviewRequests, setReviewRequests] = useState([]);
  const [currentRequest, setCurrentRequest] = useState(null);
  const [rating, setRating] = useState(0);
  const [hoverRating, setHoverRating] = useState(0);
  const [title, setTitle] = useState('');
  const [content, setContent] = useState('');
  const [loading, setLoading] = useState(false);
  const [step, setStep] = useState('prompt'); // 'prompt', 'form', 'thanks'

  useEffect(() => {
    loadReviewRequests();
  }, []);

  const loadReviewRequests = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/my-review-requests`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      if (response.data && response.data.length > 0) {
        setReviewRequests(response.data);
        setCurrentRequest(response.data[0]);
      } else {
        onClose?.();
      }
    } catch (error) {
      console.error('Error loading review requests:', error);
      onClose?.();
    }
  };

  const handleSubmit = async () => {
    if (rating === 0) {
      toast.error('Seleziona un punteggio da 1 a 4 stelle');
      return;
    }

    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      
      // Invia la recensione
      await axios.post(`${API}/reviews`, {
        rating,
        title: title || null,
        content: content || null,
        appointment_id: currentRequest?.appointment_id
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });

      // Segna la richiesta come completata
      await axios.post(`${API}/review-requests/${currentRequest?.id}/complete`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });

      setStep('thanks');
      toast.success('Grazie per la tua recensione!');
      
      setTimeout(() => {
        onSuccess?.();
        onClose?.();
      }, 3000);
      
    } catch (error) {
      console.error('Error submitting review:', error);
      toast.error('Errore durante l\'invio della recensione');
    } finally {
      setLoading(false);
    }
  };

  const handleDismiss = async () => {
    try {
      const token = localStorage.getItem('token');
      await axios.post(`${API}/review-requests/${currentRequest?.id}/dismiss`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      onClose?.();
    } catch (error) {
      console.error('Error dismissing review request:', error);
      onClose?.();
    }
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '';
    const date = new Date(dateStr);
    return date.toLocaleDateString('it-IT', { 
      day: 'numeric', 
      month: 'long', 
      year: 'numeric'
    });
  };

  if (!currentRequest) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/60 backdrop-blur-sm animate-fadeIn">
      <div 
        className="bg-white rounded-2xl shadow-2xl max-w-md w-full overflow-hidden animate-slideUp"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header decorativo */}
        <div 
          className="relative px-6 py-8 text-center"
          style={{background: 'linear-gradient(135deg, #2A9FCB 0%, #2A9FCB 100%)'}}
        >
          <button
            onClick={handleDismiss}
            className="absolute top-4 right-4 text-white/80 hover:text-white transition-colors"
          >
            <X className="w-6 h-6" />
          </button>
          
          {step === 'prompt' && (
            <>
              <div className="w-16 h-16 mx-auto mb-4 bg-white/20 rounded-full flex items-center justify-center">
                <Heart className="w-8 h-8 text-white" />
              </div>
              <h2 className="text-2xl font-bold text-white mb-2">
                La tua opinione conta!
              </h2>
              <p className="text-white/90 text-sm">
                Visita del {formatDate(currentRequest.appointment_date)}
              </p>
            </>
          )}
          
          {step === 'form' && (
            <>
              <div className="w-16 h-16 mx-auto mb-4 bg-white/20 rounded-full flex items-center justify-center">
                <Star className="w-8 h-8 text-white" />
              </div>
              <h2 className="text-2xl font-bold text-white mb-2">
                Com'è andata?
              </h2>
              <p className="text-white/90 text-sm">
                La tua recensione ci aiuta a migliorare
              </p>
            </>
          )}
          
          {step === 'thanks' && (
            <>
              <div className="w-16 h-16 mx-auto mb-4 bg-white/20 rounded-full flex items-center justify-center">
                <Gift className="w-8 h-8 text-white" />
              </div>
              <h2 className="text-2xl font-bold text-white mb-2">
                Grazie mille! 🎉
              </h2>
              <p className="text-white/90 text-sm">
                La tua opinione è preziosa per noi
              </p>
            </>
          )}
        </div>

        {/* Contenuto */}
        <div className="p-6">
          {step === 'prompt' && (
            <div className="text-center">
              <p className="text-gray-600 mb-6">
                Ciao! Speriamo che la tua visita presso <strong>PRIME VISION</strong> sia stata 
                all'altezza delle tue aspettative. Ci farebbe piacere sapere come è andata!
              </p>
              
              <p className="text-gray-500 text-sm mb-6">
                Bastano solo <strong>30 secondi</strong> per aiutarci a migliorare i nostri servizi.
              </p>

              <div className="flex gap-3">
                <button
                  onClick={handleDismiss}
                  className="flex-1 py-3 px-4 rounded-xl border border-gray-200 text-gray-600 font-medium hover:bg-gray-50 transition-all"
                >
                  Più tardi
                </button>
                <button
                  onClick={() => setStep('form')}
                  className="flex-1 py-3 px-4 rounded-xl text-white font-semibold flex items-center justify-center gap-2 hover:opacity-90 transition-all"
                  style={{background: 'linear-gradient(135deg, #2A9FCB 0%, #2A9FCB 100%)'}}
                >
                  <Star className="w-5 h-5" />
                  Lascia recensione
                </button>
              </div>
            </div>
          )}

          {step === 'form' && (
            <div>
              {/* Star Rating */}
              <div className="mb-6 text-center">
                <p className="text-sm text-gray-600 mb-3">Seleziona il tuo punteggio</p>
                <div className="flex justify-center gap-2">
                  {[1, 2, 3, 4].map((star) => (
                    <button
                      key={star}
                      type="button"
                      onClick={() => setRating(star)}
                      onMouseEnter={() => setHoverRating(star)}
                      onMouseLeave={() => setHoverRating(0)}
                      className="p-1 transition-transform hover:scale-110"
                    >
                      <Star
                        className={`w-12 h-12 transition-colors ${
                          star <= (hoverRating || rating)
                            ? 'fill-current text-yellow-400'
                            : 'text-gray-300'
                        }`}
                      />
                    </button>
                  ))}
                </div>
                {rating > 0 && (
                  <p className="text-sm mt-2 font-medium" style={{
                    color: rating === 4 ? '#10B981' : rating === 3 ? '#3B82F6' : '#F59E0B'
                  }}>
                    {rating === 4 && '⭐ Eccellente!'}
                    {rating === 3 && '👍 Molto buono'}
                    {rating === 2 && '👌 Buono'}
                    {rating === 1 && '💬 Da migliorare'}
                  </p>
                )}
              </div>

              {/* Title */}
              <div className="mb-4">
                <input
                  type="text"
                  value={title}
                  onChange={(e) => setTitle(e.target.value)}
                  placeholder="Titolo (opzionale)"
                  className="w-full px-4 py-3 border rounded-xl focus:outline-none focus:ring-2 focus:ring-[#2A9FCB]"
                  maxLength={100}
                />
              </div>

              {/* Content */}
              <div className="mb-6">
                <textarea
                  value={content}
                  onChange={(e) => setContent(e.target.value)}
                  placeholder="Racconta la tua esperienza (opzionale)"
                  className="w-full px-4 py-3 border rounded-xl focus:outline-none focus:ring-2 focus:ring-[#2A9FCB] resize-none"
                  rows={3}
                  maxLength={500}
                />
              </div>

              {/* Submit */}
              <button
                onClick={handleSubmit}
                disabled={loading || rating === 0}
                className="w-full py-4 rounded-xl text-white font-semibold flex items-center justify-center gap-2 transition-all disabled:opacity-50"
                style={{background: 'linear-gradient(135deg, #2A9FCB 0%, #2A9FCB 100%)'}}
              >
                {loading ? (
                  'Invio in corso...'
                ) : (
                  <>
                    <Send className="w-5 h-5" />
                    Invia Recensione
                  </>
                )}
              </button>
            </div>
          )}

          {step === 'thanks' && (
            <div className="text-center py-4">
              <p className="text-gray-600 mb-4">
                La tua recensione verrà esaminata dal nostro team e pubblicata sul sito.
              </p>
              <p className="text-sm text-sky-600">
                Sotto il tuo commento apparirà il badge <strong>"Utente Verificato"</strong>
              </p>
            </div>
          )}
        </div>
      </div>

      <style>{`
        @keyframes fadeIn {
          from { opacity: 0; }
          to { opacity: 1; }
        }
        @keyframes slideUp {
          from { transform: translateY(20px); opacity: 0; }
          to { transform: translateY(0); opacity: 1; }
        }
        .animate-fadeIn { animation: fadeIn 0.3s ease-out; }
        .animate-slideUp { animation: slideUp 0.4s ease-out; }
      `}</style>
    </div>
  );
};

export default ReviewRequestPopup;
